import "react-datepicker/dist/react-datepicker.css";

import FormControl from "@material-ui/core/FormControl";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { App, User } from "app-interfaces";
import { setHours, setMinutes } from "date-fns/esm";
import React from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../config/AppContext";
import { zeroPad } from "../../utils/string-utils";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type HoursMinutes = { hours: number; minutes: number };

export type TimePickerProps = {
  from: HoursMinutes;
  to: HoursMinutes;
  disabled?: boolean;
};

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(2),
    minWidth: "40%",
    maxWidth: "50%",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
  },
  position: {
    top: -20,
  },
}));

function totalOfHoursAndMinutes(from: HoursMinutes, settings: User.IUserSettings): Date {
  const sessionLength = settings.session_length;
  const sessionInterval = settings.session_interval;
  const minutes = from.minutes;
  const totalTimeExceeds60Minutes = sessionLength + sessionInterval + minutes >= 60;
  return setHours(
    setMinutes(
      new Date(),
      totalTimeExceeds60Minutes
        ? sessionLength + sessionInterval + minutes - 60
        : sessionLength + sessionInterval + minutes
    ),
    totalTimeExceeds60Minutes ? from.hours + 1 : from.hours
  );
}

function getStartDate(
  from: HoursMinutes | undefined,
  settings: User.IUserSettings | undefined
): Date {
  if (from) {
    return setHours(setMinutes(new Date(), from.minutes), from.hours);
  }
  const defaultStartHour = 8;
  return settings && from
    ? totalOfHoursAndMinutes(from, settings)
    : setHours(setMinutes(new Date(), 0), defaultStartHour);
}

function getEndDate(
  to: HoursMinutes | undefined,
  settings: User.IUserSettings | undefined,
  from: HoursMinutes | undefined
): Date {
  if (to) {
    return setHours(setMinutes(new Date(), to.minutes), to.hours);
  }
  const defaultEndHour = 22;
  return settings && from
    ? totalOfHoursAndMinutes(from, settings)
    : setHours(setMinutes(new Date(), 0), defaultEndHour);
}

function toMinutes(time: HoursMinutes): number {
  return 60 * time.hours + time.minutes;
}

function toHoursMinutes(minutes: number): HoursMinutes {
  const remainingMinutes = minutes % 60;
  const hours = (minutes - remainingMinutes) / 60;
  return { hours, minutes: remainingMinutes };
}

export function createTimeOptionsForDropdown(
  intervalInMinutes: number,
  timeStart: HoursMinutes,
  timeEnd: HoursMinutes
): HoursMinutes[] {
  const maxTime = 24 * 60 - intervalInMinutes;
  const startMinutes = Math.min(maxTime, toMinutes(timeStart));
  const endMinutes = Math.min(maxTime, toMinutes(timeEnd));
  const diffMinutes = endMinutes - startMinutes;
  const numberOfIntervals = Math.ceil(diffMinutes / intervalInMinutes);
  const intervals = [];

  for (let i = 0; i <= numberOfIntervals; i++) {
    intervals.push(toHoursMinutes(startMinutes + intervalInMinutes * i));
  }
  const last = intervals[intervals.length - 1];
  if (endMinutes !== maxTime) last.minutes = Math.min(last.minutes, timeEnd.minutes);

  return intervals;
}

const convertTime = (e: Date | null) => {
  if (!e) return "";
  const hours = e.getHours();
  const minutes = e.getMinutes();
  return timeString({ hours, minutes });
};

function timeString(hourMinute: HoursMinutes) {
  return `${zeroPad(hourMinute.hours)}:${zeroPad(hourMinute.minutes)}`;
}

export const TimePickerDropdown: React.FC<TimePickerProps> = (props) => {
  const { from, to, disabled } = props;
  const { settings } = useStateValue() as {
    settings: App.State["settings"];
  };
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const timeOptionsFrom = createTimeOptionsForDropdown(5, { hours: 0, minutes: 0 }, to).map(
    timeString
  );
  const timeOptionsTo = createTimeOptionsForDropdown(5, from, { hours: 23, minutes: 55 }).map(
    timeString
  );

  const [state, setState] = React.useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: getStartDate(from, settings),
    endDate: getEndDate(to, settings, from),
  });

  const handleStartChange = (date: Date | null) => {
    if (!date) return;
    setState({ ...state, startDate: date });
  };
  const handleEndChange = (date: Date | null) => {
    if (!date) return;
    setState({ ...state, endDate: date });
  };

  return (
    <div className={classes.container}>
      <FormControl className={classes.formControl}>
        <InputLabel id="demo-from-label" className={classes.position}>
          {t("From")}
        </InputLabel>
        <Select
          labelId="demo-from-label"
          id="demo-from"
          disabled={disabled}
          name="from"
          value={convertTime(state.startDate)}
          onChange={(e: any) => {
            handleStartChange(new Date("1970-01-01T" + e.target.value));
          }}
          input={<Input />}
          MenuProps={MenuProps}
        >
          {timeOptionsFrom.map((name) => (
            <MenuItem
              key={name}
              value={name}
              style={{
                fontWeight: theme.typography.fontWeightRegular,
              }}
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl className={classes.formControl}>
        <InputLabel id="demo-to-label" className={classes.position}>
          {t("To")}
        </InputLabel>
        <Select
          labelId="demo-to-label"
          id="demo-to"
          name="to"
          disabled={disabled}
          value={convertTime(state.endDate)}
          onChange={(e: any) => {
            handleEndChange(new Date("1970-01-01T" + e.target.value));
          }}
          input={<Input />}
          MenuProps={MenuProps}
        >
          {timeOptionsTo.map((name) => (
            <MenuItem
              key={name}
              value={name}
              style={{
                fontWeight: theme.typography.fontWeightRegular,
              }}
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
